import React, { useState, useEffect } from "react";
import { 
  LayoutDashboard, Users, User, Loader2, 
  Settings as SettingsIcon, LogOut, ChevronDown, HardHat, Lock, Library, 
  FolderOpen, ShieldCheck, HelpCircle, Send, X, MessageSquare, AlertCircle, 
  Bell, Check, CheckCircle2, Info, Building2, Plus, RefreshCw, UserPlus, FileText, Activity
} from "lucide-react";
import { auth, db } from "./lib/firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
// --- IMPORTS CRITIQUES AJOUTÉS ICI (collectionGroup, getDocs, setDoc) ---
import { doc, getDoc, addDoc, setDoc, collection, onSnapshot, collectionGroup, query, where, getDocs, orderBy, updateDoc, serverTimestamp } from "firebase/firestore";
import { createFirstCompany } from "./lib/invitations";

// Imports des composants
import AuthPage from "./components/AuthPage";
import SubscriptionPage from "./components/SubscriptionPage";
import BetaWelcomePage from "./components/BetaWelcomePage";
import Dashboard from "./components/Dashboard";
import DocumentsManager from "./components/DocumentsManager";
import ClientList from "./components/ClientList";
import Settings from "./components/Settings";
import Editor from "./components/Editor";
import ConstructionSites from "./components/ConstructionSites";
import PriceLibrary from "./components/PriceLibrary"; 
import AdminPanel from "./components/AdminPanel";
import InvitationAcceptPage from "./components/InvitationAcceptPage";
import Prospects from "./components/Prospects";
import Properties from "./components/Properties";
import LegalPage from "./components/LegalPage";
import CGUPage from "./components/CGUPage";
import ActivityLogs from "./components/ActivityLogs";
import InteractiveTutorial from "./components/InteractiveTutorial";
import ErrorBoundary from "./components/ui/ErrorBoundary";
import FeedbackModal from "./components/FeedbackModal";
import FeedbackAdmin from "./components/FeedbackAdmin";

// --- COMPOSANTS INTERNES ---

const UpgradeRequired = ({ onUpgrade }) => (
  <div className="flex flex-col items-center justify-center h-[60vh] text-center p-6 animate-in fade-in zoom-in duration-300">
      <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mb-6 shadow-inner"><Lock size={48} className="text-slate-400"/></div>
      <h2 className="text-3xl font-bold text-slate-800 mb-3">Fonctionnalit� Business</h2>
      <p className="text-slate-500 max-w-md mb-8 text-lg">Le suivi de chantier, les photos et la gestion des d�penses sont r�serv�s aux membres Business.</p>
      <button onClick={onUpgrade} className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 rounded-xl font-bold shadow-lg hover:scale-105 transition transform text-lg">Passer en Business (15�/mois)</button>
  </div>
);

const NotificationCenter = ({ notifications, onClose, onNavigate, onMarkAsRead, onMarkAllRead }) => {
    const unreadCount = notifications.filter(n => !n.read).length;
    return (
        <div className="absolute top-14 right-0 w-80 md:w-96 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50 animate-in fade-in zoom-in-95 origin-top-right">
            <div className="p-4 border-b border-slate-50 bg-white flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <h4 className="font-bold text-slate-800">Notifications</h4>
                    {unreadCount > 0 && <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadCount}</span>}
                </div>
                <div className="flex gap-2">
                    {unreadCount > 0 && (<button onClick={onMarkAllRead} className="text-[10px] font-bold text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition">Tout lire</button>)}
                    <button onClick={onClose} className="text-slate-400 hover:bg-slate-100 p-1 rounded-full transition"><X size={16}/></button>
                </div>
            </div>
            <div className="max-h-[400px] overflow-y-auto scrollbar-hide">
                {notifications.length === 0 ? (
                    <div className="p-12 text-center flex flex-col items-center text-slate-400"><div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mb-3"><Bell size={20} className="opacity-50"/></div><p className="text-sm">Aucune notification.</p></div>
                ) : (
                    notifications.map(n => (
                        <div key={n.id} onClick={() => { onMarkAsRead(n.id); if(n.target) { onNavigate(n.target); onClose(); } }} className={`p-4 border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition flex gap-3 items-start group ${!n.read ? 'bg-blue-50/40' : ''}`}>
                            <div className={`mt-1 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${n.type === 'success' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>{n.type === 'success' ? <CheckCircle2 size={14}/> : <Info size={14}/>}</div>
                            <div className="flex-1">
                                <div className="flex justify-between items-start mb-1"><p className={`text-sm font-bold ${!n.read ? 'text-slate-800' : 'text-slate-600'} group-hover:text-blue-600 transition-colors`}>{n.title}</p>{!n.read && <div className="w-2 h-2 bg-blue-500 rounded-full shrink-0 mt-1.5"></div>}</div>
                                <p className="text-xs text-slate-500 leading-relaxed mb-2">{n.text}</p><p className="text-[10px] text-slate-400 font-medium">{n.date}</p>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

// --- MODALE CRÉATION ENTREPRISE ---
const CreateCompanyModal = ({ user, onClose, onCompanyCreated }) => {
    const [companyName, setCompanyName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState('');

    const handleCreate = async () => {
        if (!companyName.trim()) {
            setError('Veuillez entrer un nom d\'entreprise');
            return;
        }
        setIsCreating(true);
        try {
            const companyId = await createFirstCompany(user.uid, companyName);
            setCompanyName('');
            setError('');
            onCompanyCreated(companyId);
            // La fermeture se fait via le parent si nécessaire, mais ici on appelle onCompanyCreated qui gère l'état
        } catch (e) {
            console.error(e);
            setError('Erreur lors de la création. Veuillez réessayer.');
            setIsCreating(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all scale-100">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white"><h3 className="font-bold text-xl flex items-center gap-2"><Building2 size={24}/> Créer une Entreprise</h3><p className="text-blue-100 text-sm mt-1">Donnez un nom à votre première entreprise.</p></div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="text-xs font-bold text-slate-600 uppercase mb-2 block">Nom de l'entreprise</label>
                        <input 
                            type="text"
                            className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 transition font-medium text-slate-700"
                            placeholder="Ex: BatiFlow SARL"
                            value={companyName}
                            onChange={(e) => { setCompanyName(e.target.value); setError(''); }}
                            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                            autoFocus
                            disabled={isCreating}
                        />
                    </div>
                    {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700"><AlertCircle size={16}/> {error}</div>}
                    <div className="flex justify-end gap-3 pt-2">
                        {onClose && <button onClick={onClose} disabled={isCreating} className="px-5 py-2.5 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition disabled:opacity-50">Annuler</button>}
                        <button onClick={handleCreate} disabled={!companyName.trim() || isCreating} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg transition transform active:scale-95 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">{isCreating ? <><Loader2 size={18} className="animate-spin"/> Création...</> : <>Créer</>}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- MODALE INVITATION MEMBRE ---
const InviteMemberModal = ({ user, companyId, companyName, onClose, onInviteSent }) => {
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('member');
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const ROLE_OPTIONS = [
        { value: 'viewer', label: 'Observateur', desc: 'Lecture seule : Planning et documents', color: 'slate' },
        { value: 'member', label: 'Membre', desc: 'Lecture/écriture sur les chantiers', color: 'green' },
        { value: 'manager', label: 'Chef d\'équipe', desc: 'Créer, modifier et envoyer les devis/factures', color: 'blue' },
        { value: 'admin', label: 'Admin', desc: 'Accès total : Paramètres, Banque, Équipe', color: 'purple' }
    ];

    const handleSendInvite = async () => {
        if (!inviteEmail.trim()) {
            setError('Veuillez entrer une adresse email');
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) {
            setError('Veuillez entrer une adresse email valide');
            return;
        }

        setIsSending(true);
        setError('');
        try {
            const { createInvitation } = await import('./lib/invitations');
            await createInvitation({
                companyId,
                companyName,
                email: inviteEmail.toLowerCase(),
                role: inviteRole
            });
            setSuccess(true);
            setInviteEmail('');
            setInviteRole('member');
            setTimeout(() => {
                setSuccess(false);
                onInviteSent();
                onClose();
            }, 2000);
        } catch (e) {
            console.error('Erreur invitation:', e);
            setError('Erreur lors de l\'envoi de l\'invitation. Vérifiez vos droits d\'accès.');
            setIsSending(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all scale-100">
                <div className="bg-gradient-to-r from-green-600 to-emerald-700 p-6 text-white">
                    <h3 className="font-bold text-xl flex items-center gap-2"><UserPlus size={24}/> Ajouter un membre</h3>
                    <p className="text-green-100 text-sm mt-1">Invitez quelqu'un à rejoindre {companyName}</p>
                </div>
                <div className="p-6 space-y-4">
                    {success ? (
                        <div className="flex flex-col items-center py-8">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                <Check size={32} className="text-green-600" />
                            </div>
                            <p className="text-lg font-bold text-slate-800">Invitation envoyée !</p>
                            <p className="text-sm text-slate-500 mt-1">{inviteEmail} va recevoir une invitation.</p>
                        </div>
                    ) : (
                        <>
                            <div>
                                <label className="text-xs font-bold text-slate-600 uppercase mb-2 block">Email du membre</label>
                                <input 
                                    type="email"
                                    className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-green-500 transition font-medium text-slate-700"
                                    placeholder="jean@example.com"
                                    value={inviteEmail}
                                    onChange={(e) => { setInviteEmail(e.target.value); setError(''); }}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSendInvite()}
                                    autoFocus
                                    disabled={isSending}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-600 uppercase mb-3 block">Rôle</label>
                                <div className="space-y-2">
                                    {ROLE_OPTIONS.map(role => (
                                        <button
                                            key={role.value}
                                            onClick={() => setInviteRole(role.value)}
                                            disabled={isSending}
                                            className={`w-full text-left p-3 rounded-lg border-2 transition ${
                                                inviteRole === role.value
                                                    ? 'border-green-500 bg-green-50'
                                                    : 'border-slate-200 hover:border-green-300 bg-white'
                                            } disabled:opacity-50`}
                                        >
                                            <p className="font-bold text-slate-800">{role.label}</p>
                                            <p className="text-xs text-slate-500 mt-0.5">{role.desc}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700"><AlertCircle size={16}/> {error}</div>}
                            <div className="flex justify-end gap-3 pt-2">
                                <button onClick={onClose} disabled={isSending} className="px-5 py-2.5 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition disabled:opacity-50">Annuler</button>
                                <button onClick={handleSendInvite} disabled={!inviteEmail.trim() || isSending} className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg transition transform active:scale-95 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">{isSending ? <><Loader2 size={18} className="animate-spin"/> Envoi...</> : <>Envoyer l'invitation</>}</button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const SupportModal = ({ user, onClose }) => {
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [sent, setSent] = useState(false);
    const [isSending, setIsSending] = useState(false);

    const handleSend = async () => {
        if(!subject || !message) return;
        setIsSending(true);
        await new Promise(r => setTimeout(r, 800));
        try {
            await addDoc(collection(db, 'support_tickets'), { userId: user.uid, userEmail: user.email, subject, message, status: 'open', date: new Date().toISOString() });
            setSent(true); setTimeout(onClose, 2500);
        } catch (e) { console.error(e); setIsSending(false); }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all scale-100 relative">
                <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-6 text-white flex justify-between items-start"><div><h3 className="font-bold text-xl flex items-center gap-2"><HelpCircle size={24} className="text-blue-400"/> Besoin d'aide ?</h3><p className="text-slate-400 text-sm mt-1">Notre équipe technique vous répond sous 24h.</p></div><button onClick={onClose} className="text-slate-400 hover:text-white transition bg-white/10 p-1.5 rounded-full"><X size={20}/></button></div>
                {sent ? (<div className="p-12 text-center animate-in zoom-in duration-300 flex flex-col items-center"><div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6 shadow-sm"><Send size={32} className="ml-1" /></div><h3 className="text-2xl font-bold text-slate-800 mb-2">Message envoyé !</h3><p className="text-slate-500">Demande prise en compte.</p></div>) : (<div className="p-6 space-y-5"><div><label className="text-xs font-bold text-slate-500 uppercase mb-1.5 block ml-1">Sujet</label><div className="relative"><div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400"><AlertCircle size={18}/></div><input className="w-full border border-slate-200 bg-slate-50 rounded-xl pl-10 pr-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 transition font-medium text-slate-700" placeholder="Sujet..." value={subject} onChange={e=>setSubject(e.target.value)} autoFocus /></div></div><div><label className="text-xs font-bold text-slate-500 uppercase mb-1.5 block ml-1">Message</label><div className="relative"><div className="absolute top-3 left-3 pointer-events-none text-slate-400"><MessageSquare size={18}/></div><textarea rows="5" className="w-full border border-slate-200 bg-slate-50 rounded-xl pl-10 pr-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 transition text-slate-700 resize-none" placeholder="Détails..." value={message} onChange={e=>setMessage(e.target.value)}/></div></div><div className="pt-4 flex justify-end gap-3"><button onClick={onClose} className="px-5 py-2.5 rounded-xl font-bold text-slate-500 hover:bg-slate-100 transition">Annuler</button><button onClick={handleSend} disabled={!subject || !message || isSending} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg hover:shadow-xl transition transform active:scale-95 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">{isSending ? <><Loader2 size={18} className="animate-spin"/> Envoi...</> : <><Send size={18}/> Envoyer</>}</button></div></div>)}
            </div>
        </div>
    );
};

// --- HELPER: Traduction des rôles ---
const getRoleLabel = (role) => {
    const roles = {
        'owner': 'Propriétaire',
        'admin': 'Administrateur',
        'manager': 'Chef d\'équipe',
        'editor': 'Éditeur',
        'member': 'Membre',
        'viewer': 'Observateur'
    };
    return roles[role] || role || 'Membre';
};

import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import SignaturePage from './components/SignaturePage';

// --- APP PRINCIPALE ---
export default function App() {
  // Utiliser window.location pour le check initial, avant tout hook
  const isSignaturePage = window.location.pathname.startsWith('/sign/');
  
  // Check de la route signature AVANT tout le reste
  // Cela évite l'exécution de tous les useEffect d'authentification
  if (isSignaturePage) {
    return (
      <Routes>
        <Route path="/sign/:token" element={<SignaturePage />} />
      </Routes>
    );
  }
  
  return <MainApp />;
}

// Composant principal avec toute la logique d'authentification
function MainApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [userPlan, setUserPlan] = useState('free'); 
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); 
  const [appLoading, setAppLoading] = useState(true);
  
  const [activeTab, setActiveTab] = useState("dashboard");
  const [viewMode, setViewMode] = useState("list");
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('batiflow-darkmode');
    return saved === 'true';
  });
  const [showSupport, setShowSupport] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const [showTutorial, setShowTutorial] = useState(() => {
    const completed = localStorage.getItem('batiflow-tutorial-completed');
    return !completed; // Afficher le tutoriel si pas encore complété
  });
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [showInviteMember, setShowInviteMember] = useState(false);
  const [currentDocId, setCurrentDocId] = useState(null); 
  const [docType, setDocType] = useState("quote");
  
  // Gestion multi-entreprise & états de chargement
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [userCompanies, setUserCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [isRepairing, setIsRepairing] = useState(false); // État pour le bouton de réparation
  const [prospectData, setProspectData] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState(null); // ID du chantier sélectionné depuis le planning

  const [notifications, setNotifications] = useState([]);
  const [notificationListener, setNotificationListener] = useState(null);

  const markAsRead = async (id) => {
    if (!user || !activeCompanyId) return;
    try {
      const notifRef = doc(db, 'companies', activeCompanyId, 'notifications', id);
      await updateDoc(notifRef, {
        read: true,
        [`readBy.${user.uid}`]: true
      });
    } catch (e) {
      console.error('Error marking notification as read:', e);
    }
  };

  const markAllAsRead = async () => {
    if (!user || !activeCompanyId) return;
    try {
      const unreadNotifs = notifications.filter(n => !n.read);
      for (const notif of unreadNotifs) {
        const notifRef = doc(db, 'companies', activeCompanyId, 'notifications', notif.id);
        await updateDoc(notifRef, {
          read: true,
          [`readBy.${user.uid}`]: true
        });
      }
    } catch (e) {
      console.error('Error marking all as read:', e);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Email admin pour accès complet feedbacks et super admin
  const ADMIN_EMAIL = "2j.habitat@gmail.com"; 
  const isAdmin = user && user.email === ADMIN_EMAIL;
  
  // Fonction pour logger l'activité utilisateur
  const logActivity = async (action, details = {}) => {
    if (!user || !activeCompanyId) return;
    try {
      await addDoc(collection(db, 'companies', activeCompanyId, 'activityLogs'), {
        userId: user.uid,
        userEmail: user.email,
        action,
        details,
        timestamp: new Date().toISOString(),
        createdAt: new Date()
      });
    } catch (e) {
      console.error('Error logging activity:', e);
    }
  };
  
  // Effet pour gérer le mode sombre
  useEffect(() => {
    localStorage.setItem('batiflow-darkmode', darkMode);
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Gérer la création de devis/facture depuis un bien
  useEffect(() => {
    const handleCreateFromProperty = (event) => {
      const propertyData = event.detail;
      setProspectData(propertyData);
      setActiveTab('editor');
    };

    window.addEventListener('createFromProperty', handleCreateFromProperty);
    return () => window.removeEventListener('createFromProperty', handleCreateFromProperty);
  }, []);

  useEffect(() => {
    let unsubCompanies = null;
    const unsubscribeAuth = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUser(u);
        try {
          if (u.isAnonymous) { 
              setSubscriptionStatus('active'); setUserPlan('business_monthly'); 
          } else {
             const userDoc = await getDoc(doc(db, 'users', u.uid));
             if (userDoc.exists()) { 
                 const data = userDoc.data(); 
                 setSubscriptionStatus(data.subscriptionStatus || 'inactive'); 
                 setUserPlan(data.plan || 'free'); 
             } else { 
                 setSubscriptionStatus('inactive'); 
             }
          }
        } catch (e) { setSubscriptionStatus('active'); setUserPlan('business_monthly'); }

        try {
            const params = new URLSearchParams(window.location.search);
            const token = params.get('token') || params.get('t');
            if (token) {
              const { acceptInvitation } = await import('./lib/invitations');
              try {
                const res = await acceptInvitation(token);
                if (res && res.companyId) {
                  setActiveCompanyId(res.companyId);
                  params.delete('token'); params.delete('t');
                  window.history.replaceState({}, document.title, window.location.pathname + (params.toString() ? ('?' + params.toString()) : ''));
                }
              } catch (err) { console.warn(err); }
            }
            
            // Gérer les liens directs vers les devis/factures depuis les emails
            const tab = params.get('tab');
            const docId = params.get('docId');
            if (tab && docId) {
              if (tab === 'quotes') {
                setActiveTab('editor');
                setDocType('quote');
                setCurrentDocId(docId);
              } else if (tab === 'invoices') {
                setActiveTab('editor');
                setDocType('invoice');
                setCurrentDocId(docId);
              }
              // Nettoyer les paramètres URL après traitement
              params.delete('tab');
              params.delete('docId');
              window.history.replaceState({}, document.title, window.location.pathname + (params.toString() ? ('?' + params.toString()) : ''));
            }
        } catch (err) { console.warn(err); }

        unsubCompanies = subscribeToUserCompanies(u.uid);

      } else {
         setUser(null); setSubscriptionStatus(null); setUserCompanies([]); setActiveCompanyId(null);
         setLoadingCompanies(true);
         if (unsubCompanies) unsubCompanies(); 
      }
      setAppLoading(false);
    });
    return () => { unsubscribeAuth(); if (unsubCompanies) unsubCompanies(); };
  }, []);

  // Listener pour les notifications en temps réel
  useEffect(() => {
    if (!user || !activeCompanyId) {
      setNotifications([]);
      if (notificationListener) {
        notificationListener();
        setNotificationListener(null);
      }
      return;
    }

    const notificationsQuery = query(
      collection(db, 'companies', activeCompanyId, 'notifications'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      const notifs = snapshot.docs.map(doc => {
        const data = doc.data();
        const readByUser = data.readBy && data.readBy[user.uid];
        return {
          id: doc.id,
          ...data,
          read: readByUser || false,
          date: formatNotificationDate(data.createdAt)
        };
      });
      setNotifications(notifs);
    }, (error) => {
      console.error('Error loading notifications:', error);
    });

    setNotificationListener(() => unsubscribe);

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user, activeCompanyId]);

  const formatNotificationDate = (timestamp) => {
    if (!timestamp) return 'À l\'instant';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };

  const subscribeToUserCompanies = (userId) => {
    setLoadingCompanies(true);
    const userRef = doc(db, 'users', userId);
    const companyUnsubscribers = [];
    
    // Écoute le document utilisateur pour récupérer les companyIds
    const unsubscribeUser = onSnapshot(userRef, (userSnap) => {
      const userData = userSnap.exists() ? userSnap.data() : {};
      const companyIds = userData.companyIds || [];
      
      // Nettoyer les anciens listeners
      companyUnsubscribers.forEach(unsub => unsub());
      companyUnsubscribers.length = 0;
      
      if (companyIds.length > 0) {
        const companiesMap = new Map();
        let loadedCount = 0;
        
        // Écouter en temps réel CHAQUE entreprise (pour voir les changements de plan)
        companyIds.forEach(companyId => {
          try {
            const companyRef = doc(db, 'companies', companyId);
            const memberRef = doc(db, 'companies', companyId, 'members', userId);
            
            // Écouter les données de l'entreprise
            const unsubCompany = onSnapshot(companyRef, async (companySnap) => {
              if (companySnap.exists()) {
                const companyData = { id: companyId, ...companySnap.data() };
                
                // Récupérer le rôle de l'utilisateur dans cette entreprise
                try {
                  const memberSnap = await getDoc(memberRef);
                  if (memberSnap.exists()) {
                    companyData.role = memberSnap.data().role || memberSnap.data().roles || 'member';
                  } else if (companyData.ownerUid === userId) {
                    companyData.role = 'owner';
                  } else {
                    companyData.role = 'viewer';
                  }
                } catch (err) {
                  console.warn(`[App] Error fetching member role for ${companyId}:`, err);
                  // Si on ne peut pas récupérer le rôle, vérifier si c'est le propriétaire
                  companyData.role = companyData.ownerUid === userId ? 'owner' : 'viewer';
                }
                
                companiesMap.set(companyId, companyData);
              } else {
                console.warn(`[App] Company ${companyId} not found in Firestore`);
              }
              loadedCount++;
              
              // Une fois que tous les listeners sont établis, mettre à jour l'état
              if (loadedCount === companyIds.length) {
                const companies = Array.from(companiesMap.values());
                setUserCompanies(companies);
                setLoadingCompanies(false);
              }
            }, async (err) => {
              console.warn(`[App] Error listening to company ${companyId}:`, err);
              
              // Si l'entreprise n'existe plus ou l'utilisateur n'a plus accès, la retirer de son profil
              if (err.code === 'permission-denied' || err.message.includes('Missing or insufficient permissions')) {
                try {
                  const updatedCompanyIds = companyIds.filter(id => id !== companyId);
                  await setDoc(doc(db, 'users', userId), { companyIds: updatedCompanyIds }, { merge: true });
                } catch (cleanupErr) {
                  console.warn(`[App] Failed to cleanup company ${companyId}:`, cleanupErr);
                }
              }
              
              loadedCount++;
              if (loadedCount === companyIds.length) {
                const companies = Array.from(companiesMap.values());
                setUserCompanies(companies);
                setLoadingCompanies(false);
              }
            });
            companyUnsubscribers.push(unsubCompany);
          } catch (err) {
            console.warn(`[App] Error setting up listener for company ${companyId}:`, err);
            loadedCount++;
            if (loadedCount === companyIds.length) {
              const companies = Array.from(companiesMap.values());
              setUserCompanies(companies);
              setLoadingCompanies(false);
            }
          }
        });
      } else {
        setUserCompanies([]);
        setLoadingCompanies(false);
      }
      
    }, (err) => {
      console.warn('[App] Error loading user doc', err);
      setLoadingCompanies(false);
    });

    return () => {
      unsubscribeUser();
      companyUnsubscribers.forEach(unsub => unsub());
    };
  };

  // --- FONCTION DE RÉPARATION ROBUSTE ---
  const handleRepairProfile = async () => {
      if (!user) return;
      setIsRepairing(true);
      console.log("Démarrage de la réparation du profil...");
      
      try {
        // 1. Recherche via CollectionGroup (trouve tous les docs 'members' avec mon UID)
        // ATTENTION : Cette requête peut nécessiter un INDEX Firestore. Vérifiez la console (F12) !
        const recoveryQuery = query(collectionGroup(db, 'members'), where('uid', '==', user.uid));
        const recoverySnap = await getDocs(recoveryQuery);
        
        if (!recoverySnap.empty) {
            // On récupère les IDs des parents (qui sont les entreprises)
            const foundCompanyIds = recoverySnap.docs.map(d => d.ref.parent.parent.id).filter(Boolean);
            const uniqueIds = [...new Set(foundCompanyIds)];
            
            if (uniqueIds.length > 0) {
                // 2. On met à jour le profil utilisateur avec setDoc + merge
                await setDoc(doc(db, 'users', user.uid), { 
                    companyIds: uniqueIds 
                }, { merge: true });
                
                alert("Profil resynchronisé ! Vos entreprises devraient apparaître.");
            } else {
                alert("Aucune entreprise trouvée. Veuillez en créer une.");
            }
        } else {
            alert("Aucune association trouvée dans la base de données. Vous devez créer une entreprise.");
        }
      } catch (err) {
          console.error("ERREUR DE RÉPARATION :", err);
          // C'est souvent ici que l'erreur d'index apparaît
          if(err.message && err.message.includes('index')) {
             alert("Une configuration (Index) manque dans Firebase. Ouvrez la console développeur (F12) et cliquez sur le lien fourni par Firebase.");
          } else {
             alert("Erreur technique. Vérifiez la console pour plus de détails.");
          }
      }
      setIsRepairing(false);
  };

  // --- AUTO-SÉLECTION ---
  useEffect(() => {
    if (loadingCompanies) return;
    if (userCompanies.length > 0 && !activeCompanyId) {
        setActiveCompanyId(userCompanies[0].id);
    }
    if (activeCompanyId && userCompanies.length > 0 && !userCompanies.find(c => c.id === activeCompanyId)) {
        setActiveCompanyId(userCompanies[0].id);
    }
  }, [loadingCompanies, userCompanies, activeCompanyId]);

  // --- DÉTECTION DES PARAMÈTRES URL POUR OUVRIR DIRECTEMENT UN DOCUMENT ---
  useEffect(() => {
    if (!user || !activeCompanyId) return;
    
    const urlParams = new URLSearchParams(location.search);
    const docIdParam = urlParams.get('docId');
    const docTypeParam = urlParams.get('docType');
    const tabParam = urlParams.get('tab');
    
    if (docIdParam && docTypeParam && tabParam === 'gestion') {
      // Ouvrir le document en mode édition
      setActiveTab('gestion');
      setViewMode('edit');
      setCurrentDocId(docIdParam);
      setDocType(docTypeParam);
      
      // Nettoyer l'URL après traitement
      navigate(location.pathname, { replace: true });
    }
  }, [user, activeCompanyId, location.search]);

  const handleLogout = () => { signOut(auth); setShowUserMenu(false); };
  
  // Vérifier l'Accès aux chantiers : UNIQUEMENT Business (pas Pro) ou Beta Testeurs
  const activeCompany = userCompanies.find(c => c.id === activeCompanyId);
  const companyPlan = activeCompany?.plan || 'free';
  const canAccessSites = (companyPlan && companyPlan.includes('business')) || userPlan === 'beta_tester';
  
  // Vérifier l'accès aux devis et factures : uniquement owner, admin, manager, editor
  const userRole = activeCompany?.role || 'viewer';
  const canAccessFinancial = ['owner', 'admin', 'manager', 'editor'].includes(userRole);
  
  // Vérifier si l'utilisateur peut créer/gérer des entreprises (Business ou Beta Testeur)
  const canManageCompanies = (companyPlan && companyPlan.includes('business')) || userPlan === 'beta_tester';
  
  // DEBUG - Décommentez pour débugger les rôles et permissions
  // useEffect(() => {
  //   console.log('[App DEBUG] activeCompanyId:', activeCompanyId);
  //   console.log('[App DEBUG] activeCompany FULL:', JSON.stringify(activeCompany, null, 2));
  //   console.log('[App DEBUG] companyPlan:', companyPlan);
  //   console.log('[App DEBUG] canAccessSites:', canAccessSites);
  //   console.log('[App DEBUG] userRole:', userRole);
  //   console.log('[App DEBUG] canAccessFinancial:', canAccessFinancial);
  //   console.log('[App DEBUG] userCompanies FULL:', JSON.stringify(userCompanies, null, 2));
  // }, [activeCompanyId, activeCompany, companyPlan, canAccessSites, userRole, canAccessFinancial, userCompanies]);

  const handleCompanyCreated = (companyId) => {
    setActiveCompanyId(companyId);
    setShowCreateCompany(false);
  };

  if (appLoading) return <div className="h-screen flex flex-col items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-blue-600 mb-4" size={48} /><p className="text-slate-500 font-medium">Démarrage...</p></div>;
  if (!user) return <ErrorBoundary><AuthPage /></ErrorBoundary>;
  
  // Si l'utilisateur a une invitation en attente, afficher la page d'acceptation
  const params = new URLSearchParams(window.location.search);
  const invitationToken = params.get('token') || params.get('t');
  if (invitationToken) {
    return <ErrorBoundary><InvitationAcceptPage user={user} /></ErrorBoundary>;
  }

  // Si l'utilisateur n'a pas d'entreprise et n'est pas en train de charger, montrer la page d'abonnement
  // MAIS si loadingCompanies est true, attendre le chargement
  if (loadingCompanies && userCompanies.length === 0) {
    return <div className="h-screen flex flex-col items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-blue-600 mb-4" size={48} /><p className="text-slate-500 font-medium">Vérification de vos affiliations...</p></div>;
  }

  // RÈGLE : Si tu as au moins UNE entreprise, tu peux accéder à l'app
  // Tu n'as pas besoin d'abonnement personnel si tu fais partie d'une entreprise
  if (userCompanies.length > 0) {
    // Tu as une entreprise, Accès autorisé (peu importe ton subscriptionStatus personnel)
    if (subscriptionStatus === 'inactive') {
      setSubscriptionStatus('active'); // Marquer comme actif puisqu'il a une entreprise
    }
  } else {
    // Tu n'as PAS d'entreprise ET tu n'es pas en train de charger
    // Si tu n'es pas chargé, afficher la page d'abonnement
    if (subscriptionStatus === 'inactive') {
      return <ErrorBoundary><BetaWelcomePage user={user} onComplete={() => { setSubscriptionStatus('active'); window.location.reload(); }} /></ErrorBoundary>;
    }
  }

  if (loadingCompanies) return <div className="h-screen flex flex-col items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-blue-600 mb-4" size={48} /><h3 className="font-bold text-slate-800 mb-1">Recherche de votre entreprise...</h3></div>;

  const menuItems = [
    { id: "dashboard", label: "Accueil", icon: LayoutDashboard },
    { id: "prospects", label: "Prospects", icon: UserPlus },
    { id: "properties", label: "Biens", icon: Building2 },
    { id: "chantiers", label: "Chantiers", icon: HardHat },
  ];
  
  // Afficher "Devis & Factures" uniquement aux managers, admins, éditeurs et propriétaires
  if (canAccessFinancial) {
    menuItems.push({ id: "gestion", label: "Devis & Factures", icon: FolderOpen });
  }
  
  menuItems.push(
    { id: "library", label: "Catalogue", icon: Library }, 
    { id: "clients", label: "Clients", icon: Users }
  );
  
  if (isAdmin) menuItems.push({ id: "admin", label: "Admin", icon: ShieldCheck });
  
  const handleOpenEditor = (id = null, type = "quote", fromProspect = null) => { 
    setCurrentDocId(id); 
    setDocType(type); 
    setProspectData(fromProspect);
    setViewMode("edit"); 
    setActiveTab("gestion"); 
  };
  const handleBackToList = () => { setViewMode("list"); setCurrentDocId(null); };
  const handleNavigate = (tabOrTarget, siteIdOrFilter = null) => { 
    // Si c'est un objet de notification avec target
    if (typeof tabOrTarget === 'object' && tabOrTarget.tab) {
      const { tab, docId, docType } = tabOrTarget;
      setActiveTab(tab);
      
      // Si c'est un document de gestion (devis/facture)
      if ((tab === 'gestion' || tab === 'devis' || tab === 'factures') && docId) {
        setViewMode('edit');
        setCurrentDocId(docId);
        if (docType) {
          setDocType(docType);
        }
      } else {
        setViewMode('list');
      }
      return;
    }
    
    // Comportement normal pour une simple chaîne
    const tab = tabOrTarget;
    setActiveTab(tab); 
    if (tab === 'gestion' || tab === 'devis' || tab === 'factures') { 
      setViewMode('list'); 
    }
    if (tab === 'chantiers' && siteIdOrFilter) {
      setSelectedSiteId(siteIdOrFilter);
    } else {
      setSelectedSiteId(null);
    }
  };

  return (
    <ErrorBoundary>
      <div className={`min-h-screen font-sans pb-24 md:pb-10 transition-colors duration-200 ${
        darkMode ? 'bg-slate-900 text-slate-100' : 'bg-slate-50 text-slate-800'
      }`} onClick={() => { setShowUserMenu(false); setShowNotifications(false); }}>
        
        {/* Tutoriel interactif au premier lancement */}
        {showTutorial && activeCompanyId && (
          <InteractiveTutorial
            onComplete={() => {
              setShowTutorial(false);
              localStorage.setItem('batiflow-tutorial-completed', 'true');
              logActivity('tutorial_completed');
            }}
            onSkip={() => {
              setShowTutorial(false);
              localStorage.setItem('batiflow-tutorial-completed', 'true');
              logActivity('tutorial_skipped');
            }}
          />
        )}
        
        {/* CORRECTION : Affichage conditionnel strict */}
        {showCreateCompany && (
            <CreateCompanyModal 
                user={user} 
                onClose={() => setShowCreateCompany(false)}
                onCompanyCreated={handleCompanyCreated} 
            />
        )}

        {showInviteMember && <InviteMemberModal user={user} companyId={activeCompanyId} companyName={userCompanies.find(c => c.id === activeCompanyId)?.name} onClose={() => setShowInviteMember(false)} onInviteSent={() => {}} />}

        {showSupport && <SupportModal user={user} onClose={() => setShowSupport(false)} />}
        {showFeedback && <FeedbackModal user={user} onClose={() => setShowFeedback(false)} />}

        {/* Bouton flottant Feedback - VERSION BÊTA */}
        <button
          onClick={() => setShowFeedback(true)}
          className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-40 bg-gradient-to-r from-orange-500 to-pink-500 text-white p-3 md:p-4 rounded-full shadow-2xl hover:shadow-orange-500/50 hover:scale-110 transition-all print:hidden flex items-center gap-2 group animate-pulse hover:animate-none"
          title="Envoyer un retour"
        >
          <MessageSquare size={20} className="md:hidden"/>
          <MessageSquare size={24} className="hidden md:block"/>
          <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap font-bold">
            Feedback Bêta
          </span>
        </button>

        <nav className="bg-slate-900 text-white p-4 sticky top-0 z-50 shadow-xl print:hidden hidden md:block">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4"><div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center font-bold text-xl cursor-pointer" onClick={() => setActiveTab('dashboard')}>B</div><div><h1 className="font-bold text-lg tracking-wide text-white">BatiFlow</h1><p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Logiciel d'Artisan</p></div></div>
            <div className="flex bg-slate-800/50 p-1 rounded-xl backdrop-blur-sm border border-slate-700/50">{menuItems.map(item => (<NavButton key={item.id} {...item} activeTab={activeTab} onClick={() => handleNavigate(item.id)} />))}</div>
            <div className="flex items-center gap-4">
                {!isAdmin && <button onClick={() => setShowSupport(true)} className="text-slate-400 hover:text-white flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-lg hover:bg-slate-800 transition"><HelpCircle size={16}/> Aide</button>}
                
                {userCompanies.length > 0 && (
                  <div className="relative">
                    <button onClick={(e) => { e.stopPropagation(); setShowCompanySelector(!showCompanySelector); }} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition text-xs font-medium">
                      <Building2 size={16} />
                      {userCompanies.find(c => c.id === activeCompanyId)?.name || 'Entreprise'}
                      <ChevronDown size={14} />
                    </button>
                    {showCompanySelector && (
                      <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl py-2 text-slate-700 border border-slate-100 z-50 animate-in fade-in zoom-in-95">
                        {userCompanies.map((company) => (
                          <button key={company.id} onClick={() => { setActiveCompanyId(company.id); setShowCompanySelector(false); }} className={`w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors text-sm ${activeCompanyId === company.id ? 'bg-blue-50 text-blue-600 font-medium' : ''}`}>
                            <Building2 size={16} />
                            <div className="flex-1">
                              <p className="font-medium">{company.name}</p>
                              <p className="text-[10px] text-slate-500">{getRoleLabel(company.role)}</p>
                            </div>
                            {activeCompanyId === company.id && <Check size={16} className="text-blue-600" />}
                          </button>
                        ))}
                        <button onClick={() => { setShowCompanySelector(false); setShowInviteMember(true); }} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors text-sm border-t border-slate-50 text-green-600 font-medium">
                            <UserPlus size={16} /> Ajouter un membre
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Bouton Mode Sombre */}
                <button 
                  onClick={() => {
                    setDarkMode(!darkMode);
                    logActivity('toggle_dark_mode', { enabled: !darkMode });
                  }}
                  className="w-9 h-9 bg-slate-800 rounded-full flex items-center justify-center text-slate-300 hover:text-white hover:bg-slate-700 transition"
                  title={darkMode ? "Mode clair" : "Mode sombre"}
                >
                  {darkMode ? '☀️' : '🌙'}
                </button>
                
                <div className="relative">
                    <button onClick={(e) => { e.stopPropagation(); setShowNotifications(!showNotifications); setShowUserMenu(false); }} className="w-9 h-9 bg-slate-800 rounded-full flex items-center justify-center text-slate-300 hover:text-white hover:bg-slate-700 transition relative">
                        <Bell size={18}/>
                        {unreadCount > 0 && <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-slate-900 animate-pulse"></span>}
                    </button>
                    {showNotifications && <NotificationCenter notifications={notifications} onClose={() => setShowNotifications(false)} onNavigate={handleNavigate} onMarkAsRead={markAsRead} onMarkAllRead={markAllAsRead}/>}
                </div>
                
                <div className="relative"><div className="flex items-center gap-3 text-slate-400 cursor-pointer hover:text-white transition p-1 rounded-lg hover:bg-slate-800" onClick={(e) => { e.stopPropagation(); setShowUserMenu(!showUserMenu); setShowNotifications(false); }}><div className="text-right hidden lg:block"><p className="text-xs font-bold text-white">{user.email}</p><p className={`text-[10px] font-bold ${isAdmin ? 'text-purple-400' : 'text-blue-400'}`}>{activeCompanyId && userCompanies.find(c => c.id === activeCompanyId) ? `Connecté à ${userCompanies.find(c => c.id === activeCompanyId).name}` : (isAdmin ? 'Super Admin' : userPlan)}</p></div><ChevronDown size={14} /></div>
                {showUserMenu && (<div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl py-2 text-slate-700 border border-slate-100 overflow-hidden z-50 animate-in fade-in zoom-in-95"><button onClick={() => setActiveTab('settings')} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors text-sm font-medium"><SettingsIcon size={16} className="text-slate-400"/> Paramètres</button><button onClick={() => { setActiveTab('activity'); setShowUserMenu(false); }} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors text-sm font-medium border-t border-slate-50"><Activity size={16} className="text-slate-400"/> Journal d'activité</button>{isAdmin && <button onClick={() => { setActiveTab('feedbacks'); setShowUserMenu(false); }} className="w-full text-left px-4 py-3 hover:bg-gradient-to-r hover:from-orange-50 hover:to-pink-50 flex items-center gap-3 transition-colors text-sm font-medium border-t border-slate-50"><MessageSquare size={16} className="text-orange-500"/> 💬 Feedbacks Bêta<span className="ml-auto bg-orange-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">ADMIN</span></button>}<button onClick={() => { setActiveTab('legal'); setShowUserMenu(false); }} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors text-sm font-medium border-t border-slate-50"><ShieldCheck size={16} className="text-slate-400"/> Mentions Légales</button><button onClick={() => { setActiveTab('cgu'); setShowUserMenu(false); }} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors text-sm font-medium border-t border-slate-50"><FileText size={16} className="text-slate-400"/> CGU / CGV</button><button onClick={handleLogout} className="w-full text-left px-4 py-3 hover:bg-red-50 text-red-600 flex items-center gap-3 transition-colors text-sm font-medium border-t border-slate-50"><LogOut size={16} /> Déconnexion</button></div>)}</div>
            </div>
          </div>
        </nav>

        <nav className="bg-slate-900 text-white p-4 sticky top-0 z-50 shadow-md md:hidden flex justify-between items-center print:hidden"><div className="flex items-center gap-2" onClick={() => setActiveTab('dashboard')}><div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center font-bold text-sm">B</div><span className="font-bold tracking-wide">BatiFlow</span></div><div className="flex items-center gap-3">{!isAdmin && <button onClick={() => setShowSupport(true)}><HelpCircle size={20} className="text-slate-400"/></button>}<div className="relative"><button onClick={(e) => { e.stopPropagation(); setShowNotifications(!showNotifications); setShowUserMenu(false); }} className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center text-slate-300 active:scale-95 transition relative"><Bell size={16}/>{unreadCount > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-slate-900 animate-pulse"></span>}</button>{showNotifications && <NotificationCenter notifications={notifications} onClose={() => setShowNotifications(false)} onNavigate={handleNavigate} onMarkAsRead={markAsRead} onMarkAllRead={markAllAsRead}/>}</div><div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center active:scale-95 transition" onClick={() => setActiveTab('settings')}><SettingsIcon size={16} /></div></div></nav>

        <main className="pt-4 md:pt-8 px-2 md:px-0">
          {!activeCompanyId && userCompanies.length === 0 ? (
            <div className="max-w-2xl mx-auto mt-16 p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-200 text-center animate-in fade-in">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <Building2 size={32} className="text-blue-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Bienvenue dans BatiFlow</h2>
              <p className="text-slate-600 mb-6">Si vous ne voyez pas votre entreprise, cliquez sur "Resynchroniser".</p>
              
              <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                {canManageCompanies ? (
                  <button 
                    onClick={() => setShowCreateCompany(true)} 
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition inline-flex items-center gap-2"
                  >
                    <Plus size={20} /> Créer une Entreprise
                  </button>
                ) : (
                  <div className="px-6 py-3 bg-slate-100 text-slate-500 font-medium rounded-lg border border-slate-300 inline-flex items-center gap-2">
                    <Lock size={20} /> Créer une Entreprise (Business uniquement)
                  </div>
                )}
                <button 
                  onClick={handleRepairProfile} 
                  disabled={isRepairing} 
                  className="px-6 py-3 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium rounded-lg transition inline-flex items-center gap-2"
                >
                    {isRepairing ? <Loader2 size={20} className="animate-spin" /> : <RefreshCw size={20} />} 
                    {isRepairing ? "Recherche..." : "Resynchroniser mon profil"}
                </button>
              </div>
            </div>
          ) : !activeCompanyId ? (
            <div className="max-w-2xl mx-auto mt-12 p-6 bg-orange-50 rounded-2xl border border-orange-200 flex items-start gap-4 animate-in fade-in">
              <AlertCircle className="text-orange-600 flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="font-bold text-orange-900 mb-1">Sélectionnez une entreprise</h3>
                <p className="text-sm text-orange-700">Vous avez plusieurs entreprises. Veuillez en s��lectionner une dans le menu pour continuer.</p>
              </div>
            </div>
          ) : (
            <>
              {/* Dashboard */}
              {activeTab === "dashboard" && (
                <Dashboard 
                  user={user} 
                  activeCompanyId={activeCompanyId} 
                  userRole={userRole}
                  userPlan={userPlan}
                  onNavigate={handleNavigate} 
                  onCreateDocument={handleOpenEditor}
                  logActivity={logActivity}
                />
              )}

              {/* Chantiers avec vérification accès */}
              {activeTab === "chantiers" && (
                canAccessSites ? (
                  <ConstructionSites 
                    user={user} 
                    activeCompanyId={activeCompanyId} 
                    initialSiteId={selectedSiteId} 
                  />
                ) : (
                  <UpgradeRequired 
                    onUpgrade={() => setSubscriptionStatus('inactive')} 
                  />
                )
              )}

              {/* Gestion documents */}
              {activeTab === "gestion" && viewMode === "list" && (
                <DocumentsManager 
                  user={user} 
                  activeCompanyId={activeCompanyId} 
                  onOpenEditor={handleOpenEditor}
                  logActivity={logActivity}
                />
              )}

              {/* Clients */}
              {activeTab === "clients" && (
                <ClientList 
                  user={user} 
                  activeCompanyId={activeCompanyId} 
                  onSelectClient={(client) => handleOpenEditor(null, "quote")}
                  logActivity={logActivity}
                />
              )}

              {/* Prospects */}
              {activeTab === "prospects" && (
                <Prospects 
                  user={user} 
                  activeCompanyId={activeCompanyId} 
                  onCreateQuote={(prospectData) => handleOpenEditor(null, "quote", prospectData)} 
                />
              )}

              {/* Biens */}
              {activeTab === "properties" && (
                <Properties 
                  user={user} 
                  activeCompanyId={activeCompanyId} 
                />
              )}

              {/* Catalogue prix */}
              {activeTab === "library" && (
                <PriceLibrary 
                  user={user} 
                  activeCompanyId={activeCompanyId} 
                />
              )}

              {/* Paramètres */}
              {activeTab === "settings" && (
                <Settings 
                  user={user} 
                  activeCompanyId={activeCompanyId} 
                  activeCompanyName={
                    userCompanies.find(c => c.id === activeCompanyId)?.name || 'Non défini'
                  } 
                />
              )}

              {/* Journal d'activité */}
              {activeTab === "activity" && (
                <ActivityLogs 
                  user={user} 
                  activeCompanyId={activeCompanyId} 
                  onBack={() => setActiveTab('dashboard')} 
                />
              )}

              {/* Admin Feedbacks Bêta */}
              {activeTab === "feedbacks" && (
                <FeedbackAdmin user={user} />
              )}

              {/* Pages légales */}
              {activeTab === "legal" && (
                <LegalPage onBack={() => setActiveTab('dashboard')} />
              )}

              {activeTab === "cgu" && (
                <CGUPage onBack={() => setActiveTab('dashboard')} />
              )}

              {/* Admin panel */}
              {activeTab === "admin" && isAdmin && <AdminPanel />} 
          {viewMode === "edit" && (activeTab === "gestion" || activeTab === "devis" || activeTab === "factures") && (
            <Editor user={user} activeCompanyId={activeCompanyId} docId={currentDocId} type={docType} prospectData={prospectData} onBack={handleBackToList} onSaveSuccess={() => console.log("Saved")} onRedirect={handleOpenEditor} logActivity={logActivity} />
          )}
            </>
          )}
        </main>

        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-2 flex justify-between items-center z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] print:hidden safe-area-pb">{menuItems.map(item => (<button key={item.id} onClick={() => handleNavigate(item.id)} className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all ${activeTab === item.id ? 'text-blue-600' : 'text-slate-400'}`}>{item.id === 'chantiers' && !canAccessSites && activeTab !== 'chantiers' && <div className="absolute top-2 right-4 w-2 h-2 bg-orange-500 rounded-full"></div>}<item.icon size={24} strokeWidth={activeTab === item.id ? 2.5 : 2} /><span className="text-[10px] font-medium">{item.label}</span></button>))}</div>
      </div>
    </ErrorBoundary>
  );
}

const NavButton = ({ label, icon: Icon, activeTab, onClick }) => (
  <button onClick={onClick} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${activeTab === onClick.name ? "bg-slate-700 text-white shadow-md" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}><Icon size={16} className={activeTab === onClick.name ? "text-orange-400" : ""} /> {label}</button>
);

